import React, { useState } from 'react'
import Typography from 'src/components/atoms/Typography'
import cometLoader from 'src/assets/comet-loader.svg'
import { IconPhotoOff, IconPhoto } from '@tabler/icons-react'
import { LayerUploadStatus } from 'src/types/layer'

type ThumbnailState = 'creating' | 'generating' | 'image' | 'empty'

const overlayClass = 'absolute inset-0 flex flex-col items-center justify-center bg-gray-700'

const CreatingThumbnail = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-loader-radial">
    <img src={cometLoader} alt="" width={40} height={40} className="animate-spin" />
  </div>
)

const GeneratingThumbnail = () => (
  <div className={overlayClass}>
    <div className="animate-pulse">
      <IconPhoto className="h-8 w-8 text-gray-400" />
    </div>
    <Typography variant="sm" className="mt-3 text-center text-gray-400">
      Your project is ready.
      <br />
      Thumbnail is being generated...
    </Typography>
  </div>
)

const EmptyThumbnail = () => (
  <div className={overlayClass}>
    <IconPhotoOff className="h-8 w-8 text-gray-400" />
    <Typography variant="sm" className="mt-2 text-gray-400">
      No image available
    </Typography>
  </div>
)

interface ProjectThumbnailProps {
  thumbnailUrl: string | null
  layerUploadStatus: LayerUploadStatus
  copaStatus?: LayerUploadStatus
}

const ProjectThumbnail: React.FC<ProjectThumbnailProps> = ({ thumbnailUrl, layerUploadStatus, copaStatus }) => {
  const isMapReady = layerUploadStatus === LayerUploadStatus.COMPLETED || layerUploadStatus === LayerUploadStatus.ERROR
  const copaSettled = copaStatus === LayerUploadStatus.COMPLETED || copaStatus === LayerUploadStatus.ERROR
  const isCreating = !isMapReady || !copaSettled
  const isThumbnailGenerating = isMapReady && copaSettled && !thumbnailUrl

  const [isLoading, setIsLoading] = useState(true)
  const [imageError, setImageError] = useState(false)

  // Retry the load on each new thumbnail URL — polling swaps a stale URL for the final one.
  React.useEffect(() => {
    setImageError(false)
    setIsLoading(true)
  }, [thumbnailUrl])

  const getThumbnailState = (): ThumbnailState => {
    if (isCreating) return 'creating'
    if (isThumbnailGenerating) return 'generating'
    if (thumbnailUrl && !imageError) return 'image'
    return 'empty'
  }

  switch (getThumbnailState()) {
    case 'creating':
      return <CreatingThumbnail />
    case 'generating':
      return <GeneratingThumbnail />
    case 'empty':
      return <EmptyThumbnail />
    default:
      return (
        <img
          src={thumbnailUrl ?? ''}
          alt=""
          className={`h-44 w-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'}`}
          onLoad={() => setIsLoading(false)}
          onError={() => {
            setIsLoading(false)
            setImageError(true)
          }}
        />
      )
  }
}

export default ProjectThumbnail
